import { useId } from "react";

import {
  useE2EAgentRunTraceSummary,
  type E2EAgentRunTraceSummary,
} from "../api/admin";
import { useAuth } from "../auth/AuthContext";
import { hideChatAndFastTrack } from "../lib/featureFlags";
import { Status } from "./ui";

function tokens(value: number | null | undefined): string {
  return value === null || value === undefined
    ? "unavailable"
    : value.toLocaleString();
}

function thinkingTokens(
  value: number | null | undefined,
  claude: boolean,
): string {
  if (value !== null && value !== undefined) return value.toLocaleString();
  return claude ? "included in output; breakdown unavailable" : "unavailable";
}

function usd(value: number | string | null | undefined): string {
  if (value === null || value === undefined) return "unavailable";
  const amount = Number(value);
  if (!Number.isFinite(amount)) return String(value);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  }).format(amount);
}

function runningTime(value: number | null | undefined): string {
  if (value === null || value === undefined || value < 0) return "unavailable";
  const seconds = Math.round(value / 1000);
  if (seconds >= 3600) {
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  }
  if (seconds >= 60) {
    return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  }
  return `${seconds}s`;
}

function isEncryptedPrompt(value: string | null | undefined): boolean {
  return Boolean(value && value.length > 24 && value.startsWith("gAAAAA"));
}

type RunTraceAgent = E2EAgentRunTraceSummary["agents"][number];

interface AgentTreeNode {
  agent: RunTraceAgent;
  children: AgentTreeNode[];
}

function buildAgentTree(agents: readonly RunTraceAgent[]): AgentTreeNode[] {
  const nodes = [...agents]
    .sort((left, right) => left.position - right.position)
    .map((agent) => ({ agent, children: [] }));
  const nodesByThread = new Map<string, AgentTreeNode>();
  for (const node of nodes) {
    if (node.agent.thread_id && !nodesByThread.has(node.agent.thread_id)) {
      nodesByThread.set(node.agent.thread_id, node);
    }
  }

  const roots: AgentTreeNode[] = [];
  for (const node of nodes) {
    const parent = node.agent.parent_thread_id
      ? nodesByThread.get(node.agent.parent_thread_id)
      : undefined;
    if (parent && parent !== node && parent.agent.depth < node.agent.depth) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
}

function AgentTreeItem({
  node,
  claude,
  costAllocated,
}: {
  node: AgentTreeNode;
  claude: boolean;
  costAllocated: boolean;
}) {
  const { agent } = node;
  const label =
    agent.nickname ??
    (agent.kind === "root" ? "Root agent" : "Unnamed subagent");
  const encryptedPrompt = isEncryptedPrompt(agent.prompt);
  const promptText = encryptedPrompt ? null : agent.prompt;
  const taskText = promptText ?? agent.task_summary;
  const taskLabel = promptText ? "Exact prompt" : "Task brief";
  const taskFallback = encryptedPrompt
    ? "Codex stores native v2 subagent prompts as encrypted content. This historical run has no readable task brief."
    : "Task brief was not retained.";
  return (
    <li className="e2e-agent-tree__branch">
      <article
        className={`e2e-trace-agent e2e-trace-agent--${agent.kind}`}
        aria-label={`${label}, ${runningTime(agent.duration_ms)}, ${usd(agent.estimated_cost_usd)}`}
      >
        <header>
          <div className="e2e-trace-agent__identity">
            <span
              className={`e2e-trace-agent__marker e2e-trace-agent__marker--${agent.kind}`}
              aria-hidden="true"
            >
              {agent.kind === "root" ? "R" : agent.position.toLocaleString()}
            </span>
            <div>
              <div className="e2e-trace-agent-badges">
                <Status tone={agent.kind === "root" ? "neutral" : "tint"}>
                  {agent.kind}
                </Status>
                {agent.role && <Status tone="neutralBg">{agent.role}</Status>}
              </div>
              <h4>{label}</h4>
              <small>
                Task <code>{agent.task_name}</code> · path{" "}
                <code>{agent.agent_path}</code>
              </small>
            </div>
          </div>
          <div className="e2e-trace-agent__cost">
            <span>{costAllocated ? "Allocated cost" : "Estimated cost"}</span>
            <strong>{usd(agent.estimated_cost_usd)}</strong>
          </div>
        </header>
        <dl className="e2e-trace-agent-usage">
          <div>
            <dt>Run time</dt>
            <dd>{runningTime(agent.duration_ms)}</dd>
          </div>
          <div>
            <dt>Model</dt>
            <dd>
              <code>{agent.model}</code>
            </dd>
          </div>
          <div>
            <dt>Input</dt>
            <dd>{tokens(agent.usage.input_tokens)}</dd>
          </div>
          <div>
            <dt>Output</dt>
            <dd>{tokens(agent.usage.output_tokens)}</dd>
          </div>
          <div>
            <dt>Thinking</dt>
            <dd>
              {thinkingTokens(agent.usage.reasoning_output_tokens, claude)}
            </dd>
          </div>
          <div>
            <dt>Total</dt>
            <dd>{tokens(agent.usage.total_tokens)}</dd>
          </div>
        </dl>
        <details className="e2e-trace-prompt">
          <summary>{taskLabel}</summary>
          <pre>{taskText ?? taskFallback}</pre>
        </details>
      </article>
      {node.children.length > 0 && (
        <ol className="e2e-agent-tree__children">
          {node.children.map((child) => (
            <AgentTreeItem
              key={`${child.agent.position}-${child.agent.agent_path}`}
              node={child}
              claude={claude}
              costAllocated={costAllocated}
            />
          ))}
        </ol>
      )}
    </li>
  );
}

function AgentHierarchy({
  agents,
  claude,
  costAllocated,
}: {
  agents: readonly RunTraceAgent[];
  claude: boolean;
  costAllocated: boolean;
}) {
  const roots = buildAgentTree(agents);
  const headingId = useId();
  return (
    <section className="e2e-agent-hierarchy" aria-labelledby={headingId}>
      <header className="e2e-agent-hierarchy__header">
        <div>
          <span className="section-kicker">Execution topology</span>
          <h4 id={headingId}>Agent hierarchy</h4>
        </div>
        <span>
          {agents.length.toLocaleString()} session
          {agents.length === 1 ? "" : "s"}
        </span>
      </header>
      {roots.length > 0 ? (
        <ol className="e2e-agent-tree" aria-label="Agent hierarchy">
          {roots.map((root) => (
            <AgentTreeItem
              key={`${root.agent.position}-${root.agent.agent_path}`}
              node={root}
              claude={claude}
              costAllocated={costAllocated}
            />
          ))}
        </ol>
      ) : (
        <div className="state state--empty">No agent sessions retained.</div>
      )}
    </section>
  );
}

export function RunTraceSummaryDetails({
  summary,
}: {
  summary: E2EAgentRunTraceSummary;
}) {
  const restrictedProductionUi = hideChatAndFastTrack();
  const incompleteSpawns = summary.spawn_attempts.filter(
    (attempt) => attempt.outcome !== "created",
  );
  const claude = summary.pricing.basis.startsWith("claude_");
  const costAllocated = summary.warnings.includes(
    "claude_subagent_cost_allocated",
  );
  const visibleWarnings = summary.warnings.filter(
    (warning) => warning !== "claude_subagent_cost_allocated",
  );
  const pricedAgents = summary.agents.filter(
    (agent) =>
      agent.cost_complete &&
      agent.estimated_cost_usd !== null &&
      agent.estimated_cost_usd !== undefined &&
      String(agent.estimated_cost_usd).trim() !== "" &&
      Number.isFinite(Number(agent.estimated_cost_usd)) &&
      Number(agent.estimated_cost_usd) >= 0,
  );
  const knownCost = pricedAgents.reduce(
    (total, agent) => total + Number(agent.estimated_cost_usd),
    0,
  );
  const showPartialCost =
    summary.estimated_cost_usd == null &&
    !summary.cost_complete &&
    summary.pricing.basis === "openai_public_list_price_estimate" &&
    pricedAgents.length > 0 &&
    Number.isFinite(knownCost);
  return (
    <>
      <div className="e2e-trace-flags">
        <Status tone={summary.cost_complete ? "success" : "warning"}>
          cost {summary.cost_complete ? "complete" : "incomplete"}
        </Status>
        <Status tone={summary.topology_complete ? "success" : "warning"}>
          topology {summary.topology_complete ? "complete" : "incomplete"}
        </Status>
        <Status tone={summary.trace_complete ? "success" : "warning"}>
          trace {summary.trace_complete ? "complete" : "incomplete"}
        </Status>
      </div>
      <dl className="diagnostic-grid e2e-trace-totals">
        <div>
          <dt>
            {showPartialCost
              ? "Known list-price subtotal"
              : "Estimated list-price cost"}
          </dt>
          <dd>
            {usd(showPartialCost ? knownCost : summary.estimated_cost_usd)}
          </dd>
        </div>
        <div>
          <dt>{restrictedProductionUi ? "Model" : "Model and tier"}</dt>
          <dd>
            {summary.model}
            {!restrictedProductionUi && ` · ${summary.service_tier}`}
          </dd>
        </div>
        <div>
          <dt>Agent sessions</dt>
          <dd>{summary.session_count.toLocaleString()}</dd>
        </div>
        <div>
          <dt>Subagents</dt>
          <dd>{summary.subagent_count.toLocaleString()}</dd>
        </div>
        <div>
          <dt>Spawn calls</dt>
          <dd>
            {summary.spawn_attempt_count.toLocaleString()} ·{" "}
            {summary.spawn_failed_count} failed ·{" "}
            {summary.spawn_unresolved_count} unresolved
          </dd>
        </div>
        <div>
          <dt>Input tokens</dt>
          <dd>{tokens(summary.usage.input_tokens)}</dd>
        </div>
        <div>
          <dt>Cached input tokens</dt>
          <dd>{tokens(summary.usage.cached_input_tokens)}</dd>
        </div>
        <div>
          <dt>Output tokens</dt>
          <dd>{tokens(summary.usage.output_tokens)}</dd>
        </div>
        <div>
          <dt>Reasoning output (thinking)</dt>
          <dd>
            {thinkingTokens(summary.usage.reasoning_output_tokens, claude)}
          </dd>
        </div>
        <div>
          <dt>Total tokens</dt>
          <dd>{tokens(summary.usage.total_tokens)}</dd>
        </div>
      </dl>
      {showPartialCost && (
        <div className="e2e-trace-price-note">
          Partial estimate from {pricedAgents.length.toLocaleString()} of{" "}
          {summary.session_count.toLocaleString()} recorded agents; full run
          cost is unavailable.
        </div>
      )}
      <div className="e2e-trace-price-note">
        Pricing snapshot <code>{summary.pricing.snapshot_id}</code> · effective{" "}
        {summary.pricing.effective_date}
      </div>
      {costAllocated && (
        <div className="e2e-trace-price-note">
          Claude per-agent costs proportionally allocate the CLI-reported
          session total using each agent&apos;s model and token mix.
        </div>
      )}
      {visibleWarnings.length > 0 && (
        <div className="build-detail-alert e2e-trace-warning">
          <strong>Trace accounting warnings</strong>
          <span>{visibleWarnings.join(" · ")}</span>
        </div>
      )}
      <AgentHierarchy
        agents={summary.agents}
        claude={claude}
        costAllocated={costAllocated}
      />
      {incompleteSpawns.length > 0 && (
        <div className="e2e-trace-spawn-failures">
          <h4>Spawn calls without an agent session</h4>
          {incompleteSpawns.map((attempt, index) => (
            <details
              key={`${attempt.call_id ?? "spawn"}-${index}`}
              className="e2e-trace-prompt"
            >
              <summary>
                {attempt.task_name ?? "Unnamed task"} · {attempt.outcome}
              </summary>
              <pre>
                {isEncryptedPrompt(attempt.prompt)
                  ? "Codex stored this native v2 task as encrypted content."
                  : (attempt.prompt ?? "Prompt was not retained.")}
              </pre>
            </details>
          ))}
        </div>
      )}
    </>
  );
}

export default function RunTraceSummaryPanel({
  runId,
  terminal,
}: {
  runId: string;
  terminal: boolean;
}) {
  const auth = useAuth();
  if (!auth.canOperate) return null;
  return <AuthorizedRunTraceSummaryPanel runId={runId} terminal={terminal} />;
}

function AuthorizedRunTraceSummaryPanel({
  runId,
  terminal,
}: {
  runId: string;
  terminal: boolean;
}) {
  const traceSummary = useE2EAgentRunTraceSummary(runId, terminal);
  return (
    <section className="e2e-admin-evidence">
      <header>
        <div>
          <span className="section-kicker">Retained model accounting</span>
          <h3>Run cost and agents</h3>
        </div>
        {traceSummary.data && (
          <span>{traceSummary.data.subagent_count} subagents</span>
        )}
      </header>
      {traceSummary.isLoading && (
        <div className="state">Loading retained cost and agent trace…</div>
      )}
      {traceSummary.error && (
        <div className="state err">
          Cost and agent trace unavailable: {traceSummary.error.message}.
        </div>
      )}
      {traceSummary.data === null && (
        <div className="state">
          {terminal
            ? "No retained trace summary. Historical runs were not backfilled."
            : "Cost and agents will appear after the model process returns its trace."}
        </div>
      )}
      {traceSummary.data && (
        <RunTraceSummaryDetails summary={traceSummary.data} />
      )}
    </section>
  );
}
